// ResponseAssetGrid — renders the safe knowledge-asset previews attached to
// an assistant turn (`return-knowledge-images-across-surfaces` PR 2, web
// chat). Compact grid of authed-fetched thumbnails that open the app's shared
// `ImageLightbox` (`unify-image-lightbox`), a "go to source" affordance that
// reuses the same citation-label resolution the inline `[doc-N]` chips use,
// and an authorized original download gated on `original_available`.
//
// The grid — not the card — owns the gallery
// (`navigate-and-localize-chat-image-lightbox`): a turn's assets form one set,
// `activeKey` (a `relation_id`) says which one is open, and the grid fetches every rendition
// itself, once each, through `useAuthedBlobUrls`. Thumbnails and the lightbox
// read that same map, so moving between siblings reuses blobs the thumbnails
// already loaded instead of refetching. Previous/next, `ArrowLeft`/`ArrowRight`,
// wrap and the `n de N` indicator all live in `ImageLightbox`, which keeps ONE
// dialog mounted for the whole set — the per-card dialog and the `navDirection`
// focus hand-off this file used to need are gone.
//
// Every asset of the turn is in the set, loading and unavailable ones
// included: navigating onto a sibling that is still loading — or whose
// rendition was revoked — keeps the lightbox open on it instead of closing it
// or skipping past it. An unavailable asset shows only the neutral placeholder:
// no caption, locator, source affordance, or download. Everything else rides in
// the slide's `details` (`ResponseAssetDetails`), which owns the download state.
//
// Every `<img>` is either a real preview backed by a same-origin
// `blob:` object URL (see `useAuthedBlobUrls`) or absent — this component
// NEVER points an `<img>` at a storage key, a signed URL, or any remote
// address, and never renders base64 image data. `relation_id` is the only
// identifier surfaced to the DOM; per `ResponseAssetProjection`'s contract
// it identifies the `chat_message_assets` row (meaningless outside the
// owning session), never the underlying global `knowledge_assets.id`, which
// this component never even receives.

import {
  ImageLightbox,
  type LightboxImage,
  type LightboxSlide,
} from "@/components/ui/image-lightbox";
import { describeAssetLocator } from "@/lib/asset-locator-labels";
import { fetchResponseAssetBlob } from "@/lib/chat";
import type { ResponseAssetProjection } from "@/lib/chat-event-schemas";
import { triggerBlobDownload } from "@/lib/reports";
import type { BlobUrlState } from "@/lib/use-authed-blob-url";
import { lightboxImageOf, useAuthedBlobUrls } from "@/lib/use-authed-blob-urls";
import { Download, ExternalLink, ImageOff, Loader } from "lucide-react";
import { type JSX, useState } from "react";
import type { Citation } from "./CitationCard";
import { resolveCitationLabel } from "./CitationToken";

// What an asset's preview is right now. Neutral local "unavailable" state —
// never a broken `<img>` pointing at a remote URL: it fires immediately when
// the DTO says there's no preview, and once the fetch itself fails (a stale
// relation, a revoked source, etc.). A key the map does not hold yet (the
// render before its fetch starts) is loading.
function previewOf(asset: ResponseAssetProjection, state: BlobUrlState | undefined): LightboxImage {
  if (!asset.preview_available) return { status: "unavailable" };
  return lightboxImageOf(state);
}

interface ResponseAssetThumbnailProps {
  asset: ResponseAssetProjection;
  image: LightboxImage;
  onOpen: () => void;
}

// Presentational: the thumbnail's three states. Only a ready one is a button.
function ResponseAssetThumbnail({
  asset,
  image,
  onOpen,
}: ResponseAssetThumbnailProps): JSX.Element {
  function renderThumbnail(): JSX.Element {
    if (image.status === "unavailable") {
      return (
        <div
          className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-md border border-border bg-muted p-1 text-center text-label-3 text-muted-foreground"
          title={asset.alt_text}
          data-testid={`response-asset-unavailable-${asset.relation_id}`}
        >
          <ImageOff size={16} aria-hidden />
          <span>No disponible</span>
        </div>
      );
    }
    if (image.status === "loading") {
      return (
        <div
          className="flex h-20 w-20 animate-pulse items-center justify-center rounded-md border border-border bg-muted"
          title={asset.alt_text}
          data-testid={`response-asset-loading-${asset.relation_id}`}
        >
          <Loader size={14} className="animate-spin text-muted-foreground" />
        </div>
      );
    }
    return (
      <button
        type="button"
        onClick={onOpen}
        className="overflow-hidden rounded-md border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Ver imagen: ${asset.alt_text}`}
        title={asset.alt_text}
      >
        <img
          src={image.url}
          alt={asset.alt_text}
          loading="lazy"
          className="h-20 w-20 object-cover transition-opacity hover:opacity-80"
        />
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid={`response-asset-${asset.relation_id}`}>
      {renderThumbnail()}
    </div>
  );
}

interface ResponseAssetDetailsProps {
  asset: ResponseAssetProjection;
  sessionId: string;
  messageId: string;
  sourceCitation: Citation | null;
  onSourceClick?: (sourceId: string) => void;
}

// Caption, locator, source affordance and original download of ONE asset — the
// `details` of its lightbox slide. It owns the download's in-flight state, so
// the grid renders it with `key={relation_id}`: moving to another asset must
// not inherit (and disable) that asset's download button.
function ResponseAssetDetails({
  asset,
  sessionId,
  messageId,
  sourceCitation,
  onSourceClick,
}: ResponseAssetDetailsProps): JSX.Element {
  const [downloading, setDownloading] = useState(false);
  const locatorText = describeAssetLocator(asset.locator);

  async function handleDownloadOriginal(): Promise<void> {
    if (!asset.original_available || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchResponseAssetBlob({
        sessionId,
        messageId,
        relationId: asset.relation_id,
        kind: "original",
      });
      const safeName = asset.citation_label.replace(/[^a-zA-Z0-9-]/g, "");
      triggerBlobDownload(blob, `${safeName || "asset"}-${asset.relation_id}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <p className="text-body-3 text-foreground">{asset.caption}</p>
      <p className="text-label-2 text-muted-foreground">{locatorText}</p>
      <div className="flex flex-wrap items-center gap-2">
        {sourceCitation && onSourceClick ? (
          <button
            type="button"
            onClick={() => onSourceClick(sourceCitation.id)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-body-3 text-foreground hover:bg-muted"
          >
            <ExternalLink size={14} aria-hidden /> Ver fuente {asset.citation_label}
          </button>
        ) : (
          <span className="text-label-2 text-muted-foreground">{asset.citation_label}</span>
        )}
        {asset.original_available ? (
          <button
            type="button"
            onClick={() => void handleDownloadOriginal()}
            disabled={downloading}
            aria-label="Descargar original"
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-body-3 text-foreground hover:bg-muted disabled:opacity-50"
          >
            {downloading ? (
              <Loader size={14} className="animate-spin" aria-hidden />
            ) : (
              <Download size={14} aria-hidden />
            )}
            Descargar original
          </button>
        ) : null}
      </div>
    </>
  );
}

interface ResponseAssetGridProps {
  assets: ResponseAssetProjection[];
  sessionId: string;
  messageId: string;
  citations: Citation[];
  onSourceClick?: (sourceId: string) => void;
}

export function ResponseAssetGrid({
  assets,
  sessionId,
  messageId,
  citations,
  onSourceClick,
}: ResponseAssetGridProps): JSX.Element | null {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Only assets the DTO says have a preview are fetched; the rest are
  // unavailable from the start and never touch the network.
  const blobUrls = useAuthedBlobUrls(
    assets.filter((asset) => asset.preview_available).map((asset) => asset.relation_id),
    (relationId) => fetchResponseAssetBlob({ sessionId, messageId, relationId, kind: "rendition" }),
  );

  if (assets.length === 0) return null;

  const items = assets.map((asset) => ({
    asset,
    image: previewOf(asset, blobUrls.get(asset.relation_id)),
  }));

  const slides: LightboxSlide[] = items.map(({ asset, image }) => {
    const base: LightboxSlide = { key: asset.relation_id, alt: asset.alt_text, image };
    // An unavailable asset shows only the neutral placeholder — no details.
    if (image.status === "unavailable") return base;
    return {
      ...base,
      details: (
        <ResponseAssetDetails
          key={asset.relation_id}
          asset={asset}
          sessionId={sessionId}
          messageId={messageId}
          sourceCitation={resolveCitationLabel(asset.citation_label, citations)}
          {...(onSourceClick ? { onSourceClick } : {})}
        />
      ),
    };
  });

  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="response-asset-grid">
      {items.map(({ asset, image }) => (
        <ResponseAssetThumbnail
          key={asset.relation_id}
          asset={asset}
          image={image}
          onOpen={() => setActiveKey(asset.relation_id)}
        />
      ))}
      <ImageLightbox
        slides={slides}
        activeKey={activeKey}
        onActiveKeyChange={setActiveKey}
        onClose={() => setActiveKey(null)}
      />
    </div>
  );
}
